import { Grid, Theme, useTheme, Tooltip, Typography, Popover } from "@mui/material";
import { IDelay, IFlightAircraft, IFlightDelay } from "@vmosolutions/oc-interfaces";
import { useState } from "react";
import { FlightDelay } from "src/pages/FlightEditor/DelaysForm/FlightDelay";
import { milli2DaysHrsMin } from "src/utils/index";

export interface IDelayCodeChip {
  delay: IDelay;
  showFullName?: boolean;
  /* if present, will pop up a small window with info on the delay */
  flightDelay?: IFlightDelay;
  aircraft?: IFlightAircraft;
  showDuration?: boolean;
}

const delayCodeChipStyles = (theme: Theme): any => ({
  height: 20,
  display: "flex",
  margin: "2px",
  borderRadius: "3px",
  fontFamily: "B612 Mono, arial, sans-serif",
  fontSize: 12,
  padding: "5px",
  alignItems: "center",
  color: "white",
  backgroundColor: theme.palette.error.dark,
  whiteSpace: "nowrap",
});

const popoverPaperStyles = (theme: Theme): any => ({
  margin: "0px 10px",
  "& .flight_delay_info": { margin: 0 },
});

export const DelayCodeChip = (props: IDelayCodeChip): JSX.Element => {
  const { delay, showFullName, flightDelay, aircraft, showDuration } = props;
  const theme: Theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<Element>(null);

  if (!delay?.title || !delay?.delayCode?.hasOwnProperty("delayNumber")) return null;

  const duration =
    showDuration && !flightDelay.hasOwnProperty("duration")
      ? "[Unknown Duration]"
      : showDuration && flightDelay && typeof flightDelay.duration === "number"
        ? ` [${milli2DaysHrsMin(flightDelay?.duration * 60000)}]`
        : "";

  const delayCode = delay?.title.trim().concat(delay?.delayCode?.delayNumber?.toString());
  const tooltip = delayCode?.concat("-", delay?.delayCode?.title);
  const displayInfoPopup = !!flightDelay?._id && !!aircraft?._id;

  return (
    <>
      <Grid
        item
        sx={{
          ...delayCodeChipStyles(theme),
          cursor: displayInfoPopup ? "pointer" : "default",
          ":hover": { backgroundColor: displayInfoPopup ? theme.palette.error.main : theme.palette.error.dark },
        }}
        onClick={(event) => {
          if (displayInfoPopup) {
            setAnchorEl(event?.currentTarget);
          }
        }}
      >
        {showFullName ? (
          <Typography variant="caption">{tooltip}</Typography>
        ) : (
          <Tooltip title={tooltip ?? ""} placement="top">
            <Typography variant="caption">
              {delayCode}
              {duration}
            </Typography>
          </Tooltip>
        )}
      </Grid>

      {displayInfoPopup ? (
        <Popover
          anchorEl={anchorEl}
          anchorReference="anchorEl"
          classes={{
            paper: popoverPaperStyles(theme),
          }}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          open={!!anchorEl}
          onClose={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation();
            setAnchorEl(null);
          }}
        >
          <FlightDelay flightDelay={flightDelay} aircraft={aircraft} />
        </Popover>
      ) : null}
    </>
  );
};
